/**
 * 15 #9 — "Chase N overdue": the submissions waiting longer than 90 days (or 60 / 30), grouped by project and by
 * whom they were sent to — one prepared follow-up email each, listing the drawings, the day they were sent and how
 * long they wait. Send it from Sara (Outgoing email on) or from your own mail app and log it; either way each
 * submission remembers it was chased and the drawing history says so. The address used last time is prefilled.
 */
import { useEffect, useMemo, useState } from 'react';
import { Mail, MailCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NotConfigured } from '@/components/ui/not-configured';
import { Segmented } from '@/components/ui/segmented';
import { formatDate, formatNumber } from '@/lib/format';
import { sdRequest } from './api';
import { chaseEmail, chaseGroups, valueKey } from './model';
import { EmailComposer } from './EmailComposer';
import { AgeText, GroupTitle } from './SdUi';

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });
const THRESHOLDS = [90, 60, 30];

/** Contacts used last time ({ 'project|recipient': { to, cc } }, recent addresses), loaded when needed. */
export function useChaseContacts(active) {
  const [contacts, setContacts] = useState({ contacts: {}, recent: [] });
  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    sdRequest('get', '/chase-contacts').then((res) => { if (alive) setContacts(res.data || { contacts: {}, recent: [] }); }).catch(() => {});
    return () => { alive = false; };
  }, [active]);
  return contacts;
}

const contactKey = (group) => `${valueKey(group.project)}|${valueKey(group.recipient)}`;

const lastChase = (rows) => rows.map((row) => row.submission.chased_at).filter(Boolean).sort().pop() || '';

/** The composer for one group (also used by a row's ✉ button for a single submission). */
export function ChaseComposer({ group, onClose, contacts, currentUserName, onChase }) {
  const known = group ? contacts.contacts?.[contactKey(group)] : null;
  const initial = useMemo(() => (group ? { ...chaseEmail(group, { sender: currentUserName }), to: (known?.to || []).join(', '), cc: (known?.cc || []).join(', ') } : {}), [group, known, currentUserName]);
  const chased = group ? lastChase(group.rows) : '';
  return (
    <EmailComposer
      open={Boolean(group)}
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={group ? `Chase ${group.recipient} · ${group.project}` : ''}
      description="A ready follow-up email listing every drawing still awaiting a reply."
      initial={initial}
      recent={contacts.recent}
      summary={group ? (
        <span>
          <b className="font-semibold text-ink">{fmt(group.rows.length)}</b> {group.rows.length === 1 ? 'submission' : 'submissions'} awaiting a reply · the oldest for <AgeText days={Math.max(...group.rows.map((row) => row.sent.age ?? 0))} className="font-bold" />
          {chased ? <> · last chased {formatDate(chased)}</> : null}
        </span>
      ) : null}
      onSubmit={(payload) => onChase(group, payload)}
      sendHint="Sends from Sara’s mailbox; the consultant’s reply comes back to you."
      testId="chase-composer"
    />
  );
}

export function ChaseDialog({ open, onOpenChange, rows, currentUserName, onChase }) {
  const [days, setDays] = useState(90);
  const [active, setActive] = useState(null);
  const contacts = useChaseContacts(open);
  const groups = useMemo(() => chaseGroups(rows, { days }), [rows, days]);
  const total = groups.reduce((sum, group) => sum + group.rows.length, 0);
  const counts = useMemo(() => Object.fromEntries(THRESHOLDS.map((value) => [value, chaseGroups(rows, { days: value }).reduce((sum, group) => sum + group.rows.length, 0)])), [rows]);

  return (
    <>
      <Dialog open={open && !active} onOpenChange={(next) => { if (!next) onOpenChange(false); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden rounded-2xl border-line bg-surface p-0 text-ink" data-testid="chase-dialog">
          <div className="flex max-h-[90vh] min-w-0 flex-col">
            <DialogHeader className="border-b border-line-2 px-5 pb-3 pt-5 text-left">
              <DialogTitle className="font-heading text-[17px] font-bold text-ink">Chase overdue replies</DialogTitle>
              <DialogDescription className="text-[13px] text-ink-3">
                One follow-up email per project and recipient. {fmt(total)} {total === 1 ? 'submission waits' : 'submissions wait'} longer than {days} days in {fmt(groups.length)} {groups.length === 1 ? 'email' : 'emails'}.
              </DialogDescription>
              <div className="pt-2">
                <Segmented
                  ariaLabel="Waiting longer than"
                  size="sm"
                  value={String(days)}
                  onChange={(value) => setDays(Number(value))}
                  options={THRESHOLDS.map((value) => ({ key: String(value), label: `${value}+ days`, count: counts[value], testId: `chase-days-${value}` }))}
                />
              </div>
            </DialogHeader>
            <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto px-5 py-4" data-testid="chase-groups">
              {groups.map((group) => {
                const chased = lastChase(group.rows);
                return (
                  <li key={group.key} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-line px-3 py-2.5" data-testid={`chase-group-${group.key}`}>
                    <span className="min-w-0 flex-1">
                      <GroupTitle label={group.project} number={group.projectNo} noNumberText="no project no." discs={group.discs} />
                      <span className="mt-0.5 block text-[12px] text-ink-3">
                        to <b className="font-semibold text-ink-2">{group.recipient}</b> · {fmt(group.rows.length)} {group.rows.length === 1 ? 'drawing' : 'drawings'}
                        {chased ? <span className="text-ink-2"> · <MailCheck className="inline h-3 w-3" aria-hidden="true" /> chased {formatDate(chased, { year: 'auto' })}</span> : null}
                      </span>
                    </span>
                    <span className="shrink-0 text-[12.5px]">oldest <AgeText days={group.oldestDays} className="font-bold" /></span>
                    <Button type="button" size="sm" variant="outline" onClick={() => setActive(group)} data-testid={`chase-write-${group.key}`}>
                      <Mail aria-hidden="true" /> Write email
                    </Button>
                  </li>
                );
              })}
              {!groups.length ? <li className="py-6 text-center text-[13px] text-ink-3">Nothing waits longer than {days} days.</li> : null}
            </ul>
            <div className="border-t border-line-2 px-5 py-3">
              <NotConfigured
                feature="email_inbound"
                layout="inline"
                title="Replies are not read from a mailbox yet"
                text="Once Incoming email is set up, replies forwarded to Sara’s mailbox are read by the AI and wait here for you to confirm. Until then, paste a reply in Record reply › From email."
                testId="chase-inbound-off"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ChaseComposer
        group={active}
        onClose={() => setActive(null)}
        contacts={contacts}
        currentUserName={currentUserName}
        onChase={async (group, payload) => {
          await onChase(group, payload);
          setActive(null);
        }}
      />
    </>
  );
}
